import { Injectable } from "@angular/core";
import { INode } from "../models/layer-node.model";
import { IRootNode } from "../models/layer-root-node.model";

@Injectable({
    providedIn: "root"
})
export class LayerNodeTreeService {

    private updatesRunningMap = {};

    /**
     * Create a ripple update to parents and childs.
     * Only one ripple per tree is allowed to avoid infinite loops
     * Returns a list with changed nodes
     *
     * @param node
     */

    nodeChanged(node: INode): INode[] {
        const root = this.getRoot(node);

        if (this.updateChainAlreadyActive(root)) {
            return [];
        }
        node.partiallyChecked = false;
        const result: INode[] = [node];
        this.beginUpdateChain(root);
        this.updateParents(node);
        this.updateChildren(node, result);
        this.endUpdateChain(root);

        return result;
    }

    /**
     * Returns nodes from the tree with a layerId set;
     *
     * @param node  nodes to search from, top down.
     * @param result all childnodes with layerId set;
     */
    getLayerBoundNodes(nodes: INode[], result: INode[] = []): INode[] {
        nodes.forEach(node => {
            if (node.layerid) {
                result.push(node);
            }
            if (node.nodes) {
                this.getLayerBoundNodes(node.nodes, result);
            }
        });

        return result;
    }

    /**
     * Return the nodes, root node
     *
     * @param node The node to search from
     */
    getRoot(node: INode): IRootNode {
        let root = node;
        while (true) {
            if (root.parent) {
                root = root.parent;
            } else {
                break;
            }
        }
        return root;
    }

    copyOpacityToChildren(parent: INode): void {
        if (parent.nodes) {
            parent.nodes.forEach(child => {
                child.opacity = parent.opacity;
                this.copyOpacityToChildren(child);
            });
        }
    }

    setCheckedOrPartiallyChecked(node: INode, defaultChecked = true) {
        const checkedChildren = node.nodes?.filter(n => n.checked);
        const partiallyCheckedChildren = node.nodes?.filter(n => n.partiallyChecked);
        if (checkedChildren?.length > 0 && !defaultChecked) {
            defaultChecked = true;
        }

        if (checkedChildren?.length === node.nodes?.length) {
            node.checked = defaultChecked;
            node.partiallyChecked = false;
        } else if (checkedChildren?.length > 0 || partiallyCheckedChildren?.length > 0) {
            node.checked = false;
            node.partiallyChecked = true;
        } else {
            node.checked = false;
            node.partiallyChecked = false;
        }
    }

    private updateParents(node: INode) {
        const parent = node.parent;
        if (parent === undefined) {
            return;
        }

        this.setCheckedOrPartiallyChecked(parent);
        this.updateParents(parent);
    }

    private updateChildren(node: INode, result: INode[]) {
        if (node.nodes === undefined) {
            return;
        }

        node.nodes.filter(n => n.ready).forEach(n => {
            if (n.checked !== node.checked) {
                result.push(n);
            }
            n.partiallyChecked = false;
            n.checked = node.checked;
            this.updateChildren(n, result);
        });
    }

    private beginUpdateChain(node: INode) {
        this.updatesRunningMap[node.id] = true;
    }

    private endUpdateChain(node: INode) {
        delete this.updatesRunningMap[node.id];
    }

    private updateChainAlreadyActive(node: INode): boolean {
        return this.updatesRunningMap[node.id];
    }
}
